import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button, Flex, Pagination, Spin, Tooltip } from "antd";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import styles from "./CustomTable.module.css";
import useMediaQuery from "shared/hooks/useMediaQuery";
import { ServiceList } from "features/service/model/serviceList";

type PaginationState = {
  current: number;
  pageSize: number;
  total: number;
};

type TableData = {
  code: string;
  nameRu: string;
  nameKy: string;
  deleted?: boolean;
  services?: ServiceList[];
  editedTime?: string | null;
  createdTime?: string | null;
};

type Props = {
  data: TableData[];
  isFetching?: boolean;
  pagination?: PaginationState;
  onPageChange?: (page: number, pageSize: number) => void;
  onEdit?: (row: TableData) => void;
  onDelete?: (row: TableData) => void;
  showService?: boolean;
};

const fmt = (v: unknown) =>
  v === null || v === undefined || v === "" ? "—" : String(v);

const CustomTableComponent = ({
  data,
  isFetching,
  pagination,
  onPageChange,
  onEdit,
  onDelete,
  showService,
}: Props) => {
  const { t } = useTranslation();
  const isScreensLg = useMediaQuery("(min-width: 768px)");

  const BASE_COLS = 3; // №, nameRu, nameKy
  const SERVICE_COLS = showService ? 1 : 0;
  const ACT_COLS = 2;
  const TOTAL_COLS = BASE_COLS + SERVICE_COLS + ACT_COLS;

  const pageOffset = useMemo(() => {
    if (!pagination) return 0;
    return (
      ((pagination.current ?? 1) - 1) * (pagination.pageSize ?? data.length)
    );
  }, [pagination, data.length]);

  return (
    <div className={styles.tableWrapper}>
      <table className={styles.table} role="table">
        <thead className={styles.thead}>
          <tr>
            <th style={{ width: 56 }}>№</th>
            <th>{t("handbook.table.nameRu")}</th>
            <th>{t("handbook.table.nameKy")}</th>
            {showService && <th>{t("handbook.table.services")}</th>}
            <th className={styles.actionsHead} colSpan={2}>
              {t("handbook.table.action")}
            </th>
          </tr>
        </thead>

        <tbody>
          {isFetching && (
            <tr>
              <td className={styles.spinnerCell} colSpan={TOTAL_COLS}>
                <Spin spinning />
              </td>
            </tr>
          )}

          {!isFetching && data?.length > 0
            ? data.map((item, idx) => (
                <tr key={item.code ?? `row-${idx}`} className={styles.row}>
                  <td data-label="№" className={styles.indexCell}>
                    {idx + 1 + pageOffset}
                  </td>

                  <td data-label={t("handbook.table.nameRu")}>
                    {fmt(item.nameRu)}
                  </td>
                  <td data-label={t("handbook.table.nameKy")}>
                    {fmt(item.nameKy)}
                  </td>

                  {showService && (
                    <td data-label={t("handbook.table.services")}>
                      <span className={styles.cellValue}>
                        {fmt(item.services?.map((s) => s.nameRu).join(", "))}
                      </span>
                    </td>
                  )}

                  <td
                    data-label={t("handbook.table.action")}
                    className={styles.textActions}
                  >
                    <div className={styles.textAction}>
                      {t("handbook.table.edit")}
                    </div>
                    <div className={styles.textActionDanger}>
                      {t("handbook.table.delete")}
                    </div>
                  </td>

                  {isScreensLg && (
                    <td className={styles.btnActions}>
                      <Flex gap="small" justify="flex-end">
                        <Tooltip title={t("handbook.table.edit")}>
                          <Button
                            className={styles.iconBtn}
                            type="primary"
                            shape="circle"
                            icon={<EditOutlined />}
                            aria-label={t("handbook.table.edit")}
                            onClick={() => onEdit?.(item)}
                          />
                        </Tooltip>
                        <Tooltip title={t("handbook.table.delete")}>
                          <Button
                            className={styles.iconBtnDanger}
                            danger
                            type="primary"
                            shape="circle"
                            icon={<DeleteOutlined />}
                            aria-label={t("handbook.table.delete")}
                            onClick={() => onDelete?.(item)}
                          />
                        </Tooltip>
                      </Flex>
                    </td>
                  )}
                </tr>
              ))
            : !isFetching && (
                <tr>
                  <td className={styles.emptyState} colSpan={TOTAL_COLS}>
                    {t("handbook.table.nothingFound") ?? "Ничего не найдено"}
                  </td>
                </tr>
              )}
        </tbody>
      </table>

      {pagination && (
        <div className={styles.paginationBar}>
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            showSizeChanger
            responsive
            onChange={(page, size) => onPageChange?.(page, size)}
            pageSizeOptions={[5, 10, 15, 20, 30, 50]}
          />
        </div>
      )}
    </div>
  );
};

export const CustomTable = React.memo(CustomTableComponent);
